import { Trans, useLingui } from '@lingui/react/macro'
import { csatHooks, customerHooks } from '$src/lib/trpcHooks'
import { CsatChip } from '$src/components/company-portal/CsatChip'
import { UserEmailBadge } from '$src/components/company-portal/UserEmailBadge'
import { Badge, Card, Center, Group, Loader, Stack, Text } from '@mantine/core'
import { AppPage } from '@markku/components'
import { IconChevronRight, IconHome } from '@tabler/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

function CompanyOverview() {
  const { t } = useLingui()
  const { companyId } = Route.useParams()
  const navigate = useNavigate()

  const { data: company, isLoading } = customerHooks.useGetById(
    Number(companyId),
  )

  const { data: csat } = csatHooks.useForCustomer(Number(companyId))
  const csatByProperty = new Map(
    (csat?.byProperty || []).map((p) => [p.propertyId, p]),
  )

  if (isLoading) {
    return (
      <AppPage
        title={t`Company portal`}
        backTarget="/company-portal"
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </AppPage>
    )
  }

  if (!company) {
    return (
      <AppPage
        title={t`Company not found`}
        backTarget="/company-portal"
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Text c="dimmed">
            <Trans>Company not found</Trans>
          </Text>
        </Center>
      </AppPage>
    )
  }

  const properties = company.properties || []

  return (
    <AppPage
      title={company.name}
      backTarget="/company-portal"
      rightSection={<UserEmailBadge />}
    >
      <Stack gap="lg">
        {properties.length > 0 && csat && (
          <CsatChip
            variant="tile"
            label={t`Portfolio satisfaction`}
            avgRating={csat.portfolio.avgRating}
            count={csat.portfolio.count}
          />
        )}

        {properties.length === 0 ? (
          <Card withBorder p="xl" radius="md">
            <Center>
              <Stack align="center" gap="xs">
                <Text c="dimmed">
                  <Trans>No properties</Trans>
                </Text>
                <Text size="sm" c="dimmed">
                  <Trans>
                    Contact Markku support to add properties to your company
                    account.
                  </Trans>
                </Text>
              </Stack>
            </Center>
          </Card>
        ) : (
          <Stack gap="sm">
            {properties.map((property) => {
              const address = property.address
              const activeSubscription = property.subscriptions?.find(
                (sub) => sub.status === 'active',
              )
              const propertyCsat = csatByProperty.get(property.id)

              return (
                <Card
                  key={property.id}
                  withBorder
                  radius="md"
                  p="md"
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate({
                      to: `/company-portal/${companyId}/properties/${property.id}`,
                    })
                  }
                >
                  <Group justify="space-between" align="center">
                    <Group gap="md" style={{ flex: 1 }}>
                      <IconHome size={20} color="var(--mantine-color-gray-6)" />
                      <Stack gap={2}>
                        <Text fw={500}>{address?.street || t`No address`}</Text>
                        {address?.city && (
                          <Text size="sm" c="dimmed">
                            {address.postalCode} {address.city}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                    <Group gap="xs">
                      <CsatChip
                        avgRating={propertyCsat?.avgRating ?? null}
                        count={propertyCsat?.count ?? 0}
                      />
                      {activeSubscription && (
                        <Badge size="sm" color="green" variant="light">
                          <Trans>Active</Trans>
                        </Badge>
                      )}
                      <IconChevronRight
                        size={20}
                        color="var(--mantine-color-gray-5)"
                      />
                    </Group>
                  </Group>
                </Card>
              )
            })}
          </Stack>
        )}
      </Stack>
    </AppPage>
  )
}

export const Route = createFileRoute('/_app/company-portal/$companyId/')({
  component: CompanyOverview,
})
